import React, { useMemo, useEffect, useState, useCallback } from 'react';
import { useAuthState } from 'react-firebase-hooks/auth';
import { CheckCircle, ChevronRight, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { auth } from '@/lib/firebase';
import {
  OnboardingStep,
  subscribeToOnboardingConfig,
  subscribeToUserOnboardingProgress,
  updateUserOnboardingProgress
} from '@/services/onboardingService';
import { BaseModal } from './modals/BaseModal';

export const StepTrackingCard: React.FC = () => {
  // State hooks - always called in the same order
  const [user] = useAuthState(auth);
  const [loading, setLoading] = useState(true);
  const [onboardingConfig, setOnboardingConfig] = useState<{
    flow?: {
      steps: OnboardingStep[];
      settings?: {
        allowStepSkipping?: boolean;
        autoSave?: boolean;
        completionRequired?: boolean;
        enableReminders?: boolean;
        inactivityTimeout?: number;
        lockCompletedSteps?: boolean;
        showProgressBar?: boolean;
        showStepNumbers?: boolean;
      };
    };
  } | null>(null);
  const [completedSteps, setCompletedSteps] = useState<string[]>([]);
  const [currentStep, setCurrentStep] = useState<OnboardingStep | null>(null);
  const [progress, setProgress] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Derived state
  const steps = useMemo(() => onboardingConfig?.flow?.steps || [], [onboardingConfig]);

  // Get all steps including substeps with full paths
  const allSteps = useMemo(() => {
    if (!onboardingConfig?.flow?.steps) return [];

    const getStepsWithPaths = (steps: OnboardingStep[], parentPath: string = ''): OnboardingStep[] => {
      return steps.flatMap(step => {
        const currentPath = parentPath ? `${parentPath}:${step.id}` : step.id;
        const stepWithPath = { ...step, path: currentPath };

        if (step.substeps) {
          return [stepWithPath, ...getStepsWithPaths(step.substeps, step.id)];
        }
        return stepWithPath;
      });
    };

    return getStepsWithPaths(onboardingConfig.flow.steps).filter(step => step.type === 'modal');
  }, [onboardingConfig]);

  const currentStepId = currentStep?.id || '';

  // Effects
  useEffect(() => {
    if (!allSteps.length) return;

    // Check completion using the full path
    const completedCount = allSteps.filter(step =>
      completedSteps.some(completedPath =>
        completedPath === step.path || completedPath.endsWith(`:${step.id}`)
      )
    ).length;

    const newProgress = Math.round((completedCount / allSteps.length) * 100);
    setProgress(newProgress);

    // Find the next incomplete step
    const nextStep = allSteps.find(step =>
      !completedSteps.some(completedPath =>
        completedPath === step.path || completedPath.endsWith(`:${step.id}`)
      )
    ) || allSteps[0];

    setCurrentStep(prev => prev?.id === nextStep?.id ? prev : nextStep);
  }, [allSteps, completedSteps]);

  useEffect(() => {
    if (!user) return;

    const unsubscribeConfig = subscribeToOnboardingConfig((config) => {
      if (config) {
        setOnboardingConfig(prev => JSON.stringify(prev) === JSON.stringify(config) ? prev : config);
      }
      setLoading(false);
    });

    return () => unsubscribeConfig();
  }, [user]);

  useEffect(() => {
    if (!user) return;

    const unsubscribeProgress = subscribeToUserOnboardingProgress(
      user.uid,
      (steps) => {
        setCompletedSteps(prev => {
          const newSteps = Array.isArray(steps) ? steps : [];
          return JSON.stringify(prev) === JSON.stringify(newSteps) ? prev : newSteps;
        });
      }
    );

    return () => unsubscribeProgress();
  }, [user]);

  // Check if step skipping is allowed
  const allowStepSkipping = useMemo(() => {
    return onboardingConfig?.flow?.settings?.allowStepSkipping !== false; // Default to true if not specified
  }, [onboardingConfig]);

  // Callbacks
  const handleContinue = useCallback(async () => {
    if (!user || !currentStep) return;

    try {
      // Mark current step as completed
      await updateUserOnboardingProgress(
        user.uid,
        currentStep.id,
        true,
        onboardingConfig?.flow?.steps
      );

      // Find the next step
      const allSteps = getAllStepsFlat(onboardingConfig?.flow?.steps || []);
      const currentIndex = allSteps.findIndex(step => step.id === currentStep.id);
      
      if (currentIndex < allSteps.length - 1) {
        // Move to next step
        const nextStep = allSteps[currentIndex + 1];
        setCurrentStep(nextStep);
        
        // If next step is a modal, open it
        if (nextStep.type === 'modal') {
          setIsModalOpen(true);
        } else {
          // Next step is not a modal, close current modal
          setIsModalOpen(false);
        }
      } else {
        // No more steps, close modal
        setIsModalOpen(false);
      }
    } catch (error) {
      console.error('Error updating onboarding progress:', error);
    }
  }, [user, currentStep, onboardingConfig]);

  // Helper function to get all steps in a flat array
  const getAllStepsFlat = (steps: OnboardingStep[] = []): OnboardingStep[] => {
    return steps.flatMap(step => {
      if (step.substeps && step.substeps.length > 0) {
        return [step, ...getAllStepsFlat(step.substeps)];
      }
      return step;
    });
  };
  
  // Handle modal close
  const handleModalClose = useCallback(() => {
    setIsModalOpen(false);
  }, []);

  // Handle going back to previous step
  const handleBack = useCallback(() => {
    if (!currentStep) return;

    const allSteps = getAllStepsFlat(onboardingConfig?.flow?.steps || []);
    const currentIndex = allSteps.findIndex(step => step.id === currentStep.id);
    
    if (currentIndex > 0) {
      const previousStep = allSteps[currentIndex - 1];
      setCurrentStep(previousStep);
      
      if (previousStep.type === 'modal') {
        setIsModalOpen(true);
      }
    }
  }, [currentStep, onboardingConfig]);

  const handleStepClick = useCallback((step: OnboardingStep) => {
    // If clicking on a modal step, open the modal
    if (step.type === 'modal') {
      setCurrentStep(step);
      setIsModalOpen(true);
      return;
    }
    
    if (!currentStep) return;

    // If step skipping is not allowed, only allow clicking on the current or completed steps
    if (!allowStepSkipping) {
      console.log('Step skipping is not allowed');
      console.log('Current step:', currentStep);
      console.log('Clicked step:', step);
      
      // For non-modal steps, check if they're in the current step group
      if (step.type === 'step_group') {
        setCurrentStep(step);
        setIsModalOpen(true);
      }

      // Find the current main step and clicked main step using paths
      const currentMainStep = steps.find(s =>
        s.id === currentStep.id ||
        completedSteps.some(path => path === s.id || path.startsWith(`${s.id}:`))
      );

      const clickedMainStep = steps.find(s =>
        s.id === step.id ||
        completedSteps.some(path => path === s.id || path.startsWith(`${s.id}:`))
      );

      if (!currentMainStep || !clickedMainStep) return;

      // Only allow clicking on steps in the current main step or already completed main steps
      const currentMainStepIndex = steps.findIndex(s => s.id === currentMainStep.id);
      const clickedMainStepIndex = steps.findIndex(s => s.id === clickedMainStep.id);

      const isCurrentMainStep = currentMainStep.id === clickedMainStep.id;
      const isPreviousMainStep = clickedMainStepIndex < currentMainStepIndex;

      // Check if the step is completed using the full path
      const isCurrentOrCompletedStep =
        step.id === currentStep.id ||
        completedSteps.some(path =>
          path === step.id ||
          path.endsWith(`:${step.id}`) ||
          path === step.path
        );

      if (!isCurrentMainStep && !isPreviousMainStep && !isCurrentOrCompletedStep) {
        return; // Prevent skipping ahead to future main steps
      }
    }

    setCurrentStep(prev => prev?.id === step.id ? prev : step);
  }, [allowStepSkipping, currentStep, steps, completedSteps]);

  if (loading || !onboardingConfig) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }


  // Render loading state
  if (loading || !onboardingConfig) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  // Early return if no current step
  if (!currentStep) {
    return (
      <div className="p-4 text-center text-gray-500">
        No active step found. Please check your onboarding configuration.
      </div>
    );
  }

  // Calculate derived values for rendering
  const isSubstep = steps.some(step =>
    step.substeps?.some(substep => substep.id === currentStep.id)
  );

  const parentStep = isSubstep
    ? steps.find(step => step.substeps?.some(substep => substep.id === currentStep.id))
    : null;

  return (
    <div className="space-y-6 p-5">
      {/* Progress Bar */}
      <div className="mb-6">
        <div className="flex justify-between mb-1">
          <span className="text-sm font-medium text-gray-700">
            {currentStep.title}
          </span>
          <span className="text-sm font-medium text-gray-500">
            {progress}% Complete
          </span>
        </div>
        <div className="h-2 w-full bg-gray-200 rounded-full overflow-hidden">
          <div
            className="h-full bg-primary transition-all duration-500"
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>

      {/* Steps List */}
      <div className="space-y-4">
        {steps.map((step) => {
          const isActive = step.id === currentStep.id ||
            (isSubstep && step.id === parentStep?.id);
          const isCompleted = completedSteps.includes(step.id);
          const hasSubsteps = step.substeps && step.substeps.length > 0;

          return (
            <div
              key={step.id}
              className={cn(
                'p-4 rounded-lg border transition-colors cursor-pointer cancelDrag',
                isActive
                  ? 'border-primary bg-primary/5'
                  : 'border-gray-200 hover:border-gray-300',
                isCompleted && 'opacity-80'
              )}
              onClick={() => handleStepClick(step)}
            >
              <div className="flex items-start">
                <div
                  className={cn(
                    'flex-shrink-0 flex items-center justify-center h-8 w-8 rounded-full mr-3 mt-0.5',
                    isCompleted
                      ? 'bg-green-100 text-green-600'
                      : isActive
                        ? 'bg-primary text-white'
                        : 'bg-gray-100 text-gray-600'
                  )}
                >
                  {isCompleted ? (
                    <CheckCircle className="h-4 w-4" />
                  ) : (
                    step.order
                  )}
                </div>
                <div className="flex-1">
                  <h4 className="font-medium">{step.title}</h4>
                  <p className="text-sm text-gray-600">{step.description}</p>

                  {/* Show substeps if any */}
                  {hasSubsteps && (
                    <div className="mt-2 pl-4 border-l-2 border-gray-200 space-y-2">
                      {step.substeps?.map((substep) => (
                        <div
                          key={substep.id}
                          className={cn(
                            'p-2 rounded text-sm flex items-center cancelDrag',
                            completedSteps.includes(substep.id)
                              ? 'text-green-600'
                              : currentStep.id === substep.id
                                ? 'text-primary font-medium'
                                : 'text-gray-600',
                            'group rounded-md -ml-2 pl-2',
                            'transition-colors duration-200',
                            !completedSteps.includes(substep.id) &&
                              substep.id !== currentStep?.id &&
                              !allowStepSkipping
                              ? 'opacity-50 cursor-not-allowed'
                              : 'hover:bg-gray-50 cursor-pointer'
                          )}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleStepClick(substep);
                          }}
                        >
                          <span className="flex-1">{substep.title}</span>
                          <ChevronRight className="h-4 w-4 text-gray-400 group-hover:text-gray-600" />
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {isActive && !isCompleted && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleContinue();
                    }}
                    className="ml-4 px-3 py-1 text-sm bg-primary text-white rounded-md hover:bg-primary/90 transition-colors self-center cancelDrag"
                  >
                    Continue
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
      {currentStep?.type === 'modal' && (
        <BaseModal
          isOpen={isModalOpen}
          onOpenChange={handleModalClose}
          onComplete={handleContinue}
          onBack={handleBack}
          step={currentStep}
        />
      )}
      {currentStep?.type === 'step_group' && (
        <BaseModal
          isOpen={isModalOpen}
          onOpenChange={handleModalClose}
          onBack={handleBack}
          onComplete={async () => {
            if (user) {
              await updateUserOnboardingProgress(
                user.uid,
                currentStep.id,
                true,
                onboardingConfig?.flow?.steps
              );
              setIsModalOpen(false);
            }
          }}
          step={currentStep}
        />
      )}

    </div>
  );
};

export default StepTrackingCard;
